import React, { useState, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useInlineCopy } from '../../hooks/useInlineCopy';
import { useCopyOverlay } from '../../hooks/useCopyOverlay';
import AuthService from '../../services/authService';
import { dispatchQuotaUpdated } from '../../utils/quotaEvent';
import {
    Modal,
    Button,
    Card,
    Row,
    Col,
    Input,
    Switch,
    Tooltip,
    Popover,
    Space,
    Badge,
    List,
    Typography,
    Divider,
    Spin,
    Empty,
    message,
} from 'antd';
import {
    QuestionCircleOutlined,
    HistoryOutlined,
    CopyOutlined,
    LoadingOutlined,
    DeleteOutlined,
    ArrowRightOutlined,
    CheckOutlined,
    SearchOutlined,
    DashboardOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import ProtectedMenuPage from '../../components/ProtectedMenuPage/ProtectedMenuPage';
import KakaoFloatButton from '../../components/KakaoFloatButton/KakaoFloatButton';
import './KeyWordVer1.css';

const { Text, Paragraph } = Typography;

const KeyWordVer1 = ({ title: _title = '키워드 추출순위 Ver1' }) => {
    const [includePriceCompare, setIncludePriceCompare] = useState(false);

    const [keyword, setKeyword] = useState('');
    const [productId, setProductId] = useState('');
    const [rankFilter, setRankFilter] = useState('');
    const [rankings, setRankings] = useState([]);
    const [keywordList, setKeywordList] = useState([]);
    const [productInfo, setProductInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [_rankLoading, setRankLoading] = useState(false);
    const [_proxyLoading, setProxyLoading] = useState(false);
    const [isProxySearchSession, setIsProxySearchSession] = useState(false);
    const [searched, setSearched] = useState(false);
    const [pMResults, setPMResults] = useState([]);

    const [showHistoryModal, setShowHistoryModal] = useState(false);

    const [historyData, setHistoryData] = useState([]);
    const [historyDataGubun, setHistoryDataGubun] = useState('keywordver1');
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const PAGE_SIZE = 10;
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const keywordRef = useRef(null);
    const productIdRef = useRef(null);

    const [showCustomInput, setShowCustomInput] = useState(false);

    const emptyQuota = {
        used: 0,
        limit: 0,
        bonus: 0,
        remaining: 0,
        day: '',
        loading: true,
        error: null,
        unlimited: false,
    };
    /** 기본 순위 조회 (keywordVer1) */
    const [quotaRank, setQuotaRank] = useState(emptyQuota);
    /** 통합검색·가격비교 (keyword1) */
    const [quotaNmain, setQuotaNmain] = useState(emptyQuota);

    const stripHtml = html => html.replace(/<[^>]*>/g, '');
    const formatPrice = price => Number(price).toLocaleString();
    const formatCategory = product => {
        return [product.category1, product.category2, product.category3, product.category4]
            .filter(Boolean)
            .join(' > ');
    };

    const applyQuotaSlice = (setQuotaFn, obj) => {
        if (!obj || typeof obj !== 'object') return false;
        const has = k => Object.prototype.hasOwnProperty.call(obj, k);
        if (
            has('used') ||
            has('limit') ||
            has('bonus') ||
            has('remaining') ||
            has('day') ||
            has('unlimited')
        ) {
            const {
                used = 0,
                limit = 0,
                bonus = 0,
                remaining = 0,
                day = '',
                unlimited = false,
            } = obj;
            setQuotaFn(q => ({
                ...q,
                used,
                limit,
                bonus,
                remaining,
                day,
                unlimited: !!unlimited,
                loading: false,
                error: null,
            }));
            return true;
        }
        return false;
    };

    // 에러 메시지 변환기
    const apiErrToMessage = (data, status) => {
        const code = data?.code;
        const detail = data?.detail;
        switch (code) {
            case 'UNAUTHORIZED':
                return '로그인이 필요합니다.';
            case 'RATE_LIMIT_EXCEEDED':
                return detail || '오늘 조회 한도를 초과했습니다.';
            case 'BAD_REQUEST':
                return detail || '요청이 올바르지 않습니다.';
            case 'SERVER_ERROR':
                return '서버 오류가 발생했습니다.';
            default:
                return (
                    detail ||
                    `요청 처리 중 오류가 발생했습니다.${status ? ` (HTTP ${status})` : ''}`
                );
        }
    };

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [rankRes, nmainRes] = await Promise.all([
                    AuthService.authAxios.get('/naver/quota/status/keywordVer1', {
                        params: { ensureRow: 1 },
                    }),
                    AuthService.authAxios.get('/naver/quota/status/keyword1', {
                        params: { ensureRow: 1 },
                    }),
                ]);
                if (!alive) return;
                if (rankRes.data?.ok) {
                    applyQuotaSlice(setQuotaRank, rankRes.data);
                } else {
                    setQuotaRank(q => ({ ...q, loading: false, error: '순위 한도 조회 실패' }));
                }
                if (nmainRes.data?.ok) {
                    applyQuotaSlice(setQuotaNmain, nmainRes.data);
                } else {
                    setQuotaNmain(q => ({ ...q, loading: false, error: '통검 한도 조회 실패' }));
                }
            } catch (err) {
                setQuotaRank(q => ({ ...q, loading: false, error: err?.message ?? '에러' }));
                setQuotaNmain(q => ({ ...q, loading: false, error: err?.message ?? '에러' }));
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    const fetchRankings = async () => {
        setLoading(true);
        setRankings([]);
        setKeywordList([]);
        setProductInfo(null);
        setSearched(true);
        setPMResults([]);
        setRankLoading(false);
        setProxyLoading(false);

        try {
            const proxyAllowed = quotaNmain.unlimited || quotaNmain.remaining > 0;
            const wantProxy = includePriceCompare; // 조회 시점의 상태를 캡처
            setIsProxySearchSession(wantProxy); // UI 표시용 상태 저장

            const msgKey = 'fetching_rankings';
            message.config({
                top: 8,
                duration: 2,
                maxCount: 1,
            });
            message.loading({
                content: '상품 정보 및 키워드 목록 로드 중...',
                key: msgKey,
                duration: 0,
            });

            // 1단계: 상품 정보 및 키워드 목록만 먼저 가져옴
            const response = await AuthService.authAxios.get(`/product`, {
                params: {
                    productId,
                    keyword,
                    onlyProductInfo: 1,
                },
            });

            applyQuotaSlice(setQuotaRank, response.data);
            // 🛠️ 시작 시점에는 차감하지 않으므로 dispatchQuotaUpdated 생략

            if (!response.data.success) {
                message.error({
                    content: apiErrToMessage(response.data),
                    key: msgKey,
                    duration: 3,
                });
                setLoading(false);
                return;
            }

            const productData = response.data.product;
            const initialRankings = response.data.rankings; // rank가 null인 상태

            setKeywordList(productData.keywordList);
            setRankings(initialRankings);
            setProductInfo({
                image: productData.image,
                title: stripHtml(productData.title),
                price: formatPrice(productData.lprice),
                link: productData.link,
                mallName: productData.mallName,
                category: formatCategory(productData),
                productId: productData.productId,
                productMid: productData.productMid,
            });

            setLoading(false); // 상품 정보 나오면 메인 로딩 종료

            // 2단계: 순위 비동기 업데이트 (V2 로직 활용)
            setRankLoading(true);
            const keywords = initialRankings.map(r => r.keyword);
            const chunkSize = 5;

            // 모든 새 순위 정보를 담을 임시 배열
            let allResults = [];

            try {
                let currentRankStep = 0;
                message.loading({
                    content: `키워드 순위 조회 중... (0%)`,
                    key: msgKey,
                    duration: 0,
                });

                for (let i = 0; i < keywords.length; i += chunkSize) {
                    const chunk = keywords.slice(i, i + chunkSize);
                    const rankRes = await AuthService.authAxios.post(`/product/v2/rankings`, {
                        productId,
                        keywords: chunk,
                    });

                    if (rankRes.data.success) {
                        const newRanks = rankRes.data.rankings;
                        allResults.push(...newRanks);

                        // 상태 업데이트 (순위 실시간 표시용)
                        setRankings(prev => {
                            const updated = prev.map(item => {
                                const match = newRanks.find(r => r.keyword === item.keyword);
                                return match
                                    ? { ...item, rank: match.rank, productMid: match.productMid }
                                    : item;
                            });
                            // 순위가 나온 것들 위주로 정렬
                            return [...updated].sort((a, b) => {
                                if (a.rank === null && b.rank === null) return 0;
                                if (a.rank === null) return 1;
                                if (b.rank === null) return -1;
                                if (a.rank === 0 && b.rank === 0) return 0;
                                if (a.rank === 0) return 1;
                                if (b.rank === 0) return -1;
                                return a.rank - b.rank;
                            });
                        });
                    }
                    currentRankStep += chunk.length;
                    const percent = Math.min(
                        100,
                        Math.round((currentRankStep / keywords.length) * 100)
                    );
                    message.loading({
                        content: `키워드 순위 조회 중... (${percent}%)`,
                        key: msgKey,
                        duration: 0,
                    });
                }
            } catch (rankErr) {
                console.error('순위 조회 실패:', rankErr);
            } finally {
                setRankLoading(false);
                // 🛠️ [순위 조회 완료] 이제 실제 차감 및 기록 저장 실행
                try {
                    // 메인 키워드의 순위 찾기
                    const mainRankObj = allResults.find(r => r.keyword === keyword);
                    const finalRank = mainRankObj ? mainRankObj.rank : 0;

                    const confirmRes = await AuthService.authAxios.post(
                        '/product/confirm-consume-v1',
                        {
                            keyword,
                            productId,
                            ranking: finalRank,
                            includePriceCompare: wantProxy,
                        }
                    );

                    if (confirmRes.data.success) {
                        applyQuotaSlice(setQuotaRank, confirmRes.data.quota);
                        dispatchQuotaUpdated('keywordVer1Rank');
                    }
                } catch (qErr) {
                    console.error('순위 차감 및 기록 저장 실패:', qErr);
                }
            }

            // 3단계: 통검(가격비교) 비동기 업데이트 (1단계 순위 조회가 모두 끝난 후)
            if (wantProxy && proxyAllowed) {
                setProxyLoading(true);

                // 모든 키워드에 대해 통검 조회 수행 (순위권 밖이라도 포함)
                const proxyQualified = allResults.map(r => ({
                    keyword: r.keyword,
                    productMid: r.productMid || productData.productMid, // 개별 productMid 없으면 기본값 사용
                }));

                if (proxyQualified.length > 0) {
                    try {
                        let currentProxyStep = 0;
                        message.loading({
                            content: `통합검색 가격비교 조회 중... (0%)`,
                            key: msgKey,
                            duration: 0,
                        });

                        // 쿼터 1회 차감 및 세션 토큰 발급
                        const sessionRes = await AuthService.authAxios.post(
                            '/product/begin-proxy-session'
                        );
                        const proxyToken = sessionRes.data.proxyToken;

                        // 5개 단위로 묶어서 처리
                        for (let i = 0; i < proxyQualified.length; i += chunkSize) {
                            const proxyChunk = proxyQualified.slice(i, i + chunkSize);
                            console.log(
                                `🌐 [KEYWORD-V1-CLIENT] 통검 조회 요청 (Chunk ${i / chunkSize + 1}):`,
                                proxyChunk.map(c => c.keyword).join(', ')
                            );
                            const proxyRes = await AuthService.authAxios.post(
                                `/product/proxy-search`,
                                {
                                    keywordList: proxyChunk,
                                    proxyToken: proxyToken, // 세션 토큰 전달
                                }
                            );

                            if (proxyRes.data.success) {
                                setPMResults(prev => [...prev, ...proxyRes.data.pMResults]);
                            }

                            currentProxyStep += proxyChunk.length;
                            const percent = Math.min(
                                100,
                                Math.round((currentProxyStep / proxyQualified.length) * 100)
                            );
                            message.loading({
                                content: `통합검색 가격비교 조회 중... (${percent}%)`,
                                key: msgKey,
                                duration: 0,
                            });
                        }
                    } catch (pErr) {
                        console.error('통검 조회 실패:', pErr);
                    }
                }
                setProxyLoading(false);

                // 🛠️ [통검 조회 완료] 이제 실제 차감 실행
                try {
                    const confirmRes = await AuthService.authAxios.post(
                        '/product/confirm-proxy-v1'
                    );
                    if (confirmRes.data.success) {
                        applyQuotaSlice(setQuotaNmain, confirmRes.data.quota);
                        dispatchQuotaUpdated('keywordVer1Nmain');
                    }
                } catch (qErr) {
                    console.error('통검 차감 실패:', qErr);
                }
            }

            // 모든 단계 종료
            message.success({ content: '조회 완료', key: msgKey, duration: 2 });
        } catch (err) {
            console.error(err);
            message.warning({
                content: '상품 정보가 없거나 조회 중 오류가 발생했습니다.',
                key: 'fetching_rankings',
                duration: 3,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = e => {
        e.preventDefault();
        if (loading) return;
        if (!keyword?.trim()) {
            message.warning('메인 키워드를 입력해주세요.');
            keywordRef.current?.focus();
            return;
        }
        if (!productId?.trim()) {
            message.warning('상품번호를 입력해주세요.');
            productIdRef.current?.focus();
            return;
        }
        if (includePriceCompare && !quotaNmain.unlimited && quotaNmain.remaining <= 0) {
            message.error('오늘 통합검색(가격비교) 한도를 모두 사용하였습니다.');
            return;
        }
        if (!quotaRank.unlimited && quotaRank.remaining <= 0) {
            message.error('오늘 기본 순위 조회 한도를 모두 사용하였습니다.');
            return;
        }
        fetchRankings();
    };

    const handleReset = () => {
        setKeyword('');
        setProductId('');
        setRankFilter('');
        setRankings([]);
        setKeywordList([]);
        setProductInfo(null);
        setSearched(false);
        if (keywordRef.current) keywordRef.current.focus();
    };

    const handleDelete = async id => {
        Modal.confirm({
            title: '정말 삭제하시겠습니까?',
            content: '삭제된 기록은 되돌릴 수 없습니다.',
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await AuthService.authAxios.post('/log/delete', { id });
                    setHistoryData(prev => prev.filter(item => item.id !== id));
                    message.success('성공적으로 삭제되었습니다.');
                } catch (err) {
                    console.error('삭제 실패:', err);
                    message.error('삭제에 실패했습니다.');
                }
            },
        });
    };

    const handleApplyInput = inputString => {
        const values = Object.fromEntries(inputString.split('|').map(pair => pair.split('=')));
        if (historyDataGubun === 'keywordver1') {
            setKeyword(values.keyword || '');
            setProductId(values.productId || '');
        } else {
            setProductId(values.syncNvMid || '');
        }
        setShowHistoryModal(false); // react-bootstrap 모달 닫기
    };

    const fetchHistory = async (nextOffset = 0, append = false, gubun = 'keywordver1') => {
        setHistoryDataGubun(gubun);

        try {
            const res = await AuthService.authAxios.get('/log/history', {
                params: {
                    gubun: gubun,
                    offset: nextOffset,
                },
            });

            if (res.data.success) {
                const newData = res.data.data;
                if (!append) {
                    setHasMore(true);
                }
                if (newData.length < PAGE_SIZE) setHasMore(false);

                if (append) {
                    setHistoryData(prev => [...prev, ...newData]);
                    setOffset(prev => prev + PAGE_SIZE);
                } else {
                    setHistoryData(newData);
                    setOffset(PAGE_SIZE);
                }
            }
        } catch (err) {
            console.error('조회 기록 불러오기 실패:', err);
        } finally {
            setHistoryLoaded(true);
        }
    };

    const { inlineCopied, handleInlineCopy } = useInlineCopy(1200);
    const kwCopy = useCopyOverlay();
    const rankCopy = useCopyOverlay();

    const visibleRankings = useMemo(
        () => rankings.filter(r => !rankFilter || r.rank <= Number(rankFilter)),
        [rankings, rankFilter]
    );

    return (
        <ProtectedMenuPage menuKey="keywordVer1">
            <div className="keyword-ver1-page">
                {/* 상단: 검색 영역 - 가로 배치 */}
                <div className="page-header-container no-header-border">
                    <Card
                        className="control-card keyword-ver1-search-card"
                        bodyStyle={{ padding: '8px 16px' }}
                    >
                        <form onSubmit={handleSubmit}>
                            {/* 1행: 검색 입력 (왼쪽 붙여서) + 버튼 (우측) */}
                            <Row
                                gutter={[16, 16]}
                                align="middle"
                                className="keyword-ver1-search-row"
                            >
                                <Col
                                    xs={24}
                                    sm={24}
                                    md={24}
                                    flex="0 0 auto"
                                    className="keyword-ver1-inputs-col"
                                >
                                    <div className="keyword-ver1-inputs-group">
                                        <div className="keyword-ver1-input-block">
                                            <label
                                                htmlFor="keyword-input"
                                                className="keyword-ver1-label"
                                            >
                                                메인키워드
                                            </label>
                                            <Input
                                                id="keyword-input"
                                                size="small"
                                                placeholder="메인 키워드 입력"
                                                value={keyword}
                                                onChange={e => setKeyword(e.target.value)}
                                                ref={keywordRef}
                                                required
                                                className="keyword-ver1-input"
                                            />
                                        </div>
                                        <div className="keyword-ver1-input-block">
                                            <label
                                                htmlFor="productId-input"
                                                className="keyword-ver1-label"
                                            >
                                                상품번호
                                            </label>
                                            <Input
                                                id="productId-input"
                                                size="small"
                                                placeholder="상품번호 or MID 입력"
                                                value={productId}
                                                onChange={e => setProductId(e.target.value)}
                                                ref={productIdRef}
                                                required
                                                className="keyword-ver1-input"
                                            />
                                        </div>
                                        <div className="keyword-ver1-input-block keyword-ver1-naver-toggle-block">
                                            <span className="keyword-ver1-label">네이버통검</span>
                                            <div className="keyword-ver1-toggle-inline">
                                                <Switch
                                                    id="naver-price-compare-switch"
                                                    checked={includePriceCompare}
                                                    onChange={setIncludePriceCompare}
                                                    disabled={
                                                        (!quotaNmain.unlimited &&
                                                            quotaNmain.remaining <= 0) ||
                                                        loading
                                                    }
                                                    aria-label="네이버 통합검색 가격비교 조회"
                                                />
                                                <Popover
                                                    content={
                                                        <div
                                                            style={{
                                                                maxWidth: '280px',
                                                                fontSize: '13px',
                                                                lineHeight: '1.6',
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    fontWeight: 600,
                                                                    marginBottom: '6px',
                                                                }}
                                                            >
                                                                네이버 통합검색 가격비교 조회
                                                            </div>
                                                            <div>
                                                                가격비교 포함 시 조회 속도가 느려질
                                                                수 있습니다.
                                                            </div>
                                                        </div>
                                                    }
                                                    trigger="hover"
                                                    placement="top"
                                                    mouseEnterDelay={0.15}
                                                    mouseLeaveDelay={0.1}
                                                >
                                                    <QuestionCircleOutlined className="keyword-ver1-tooltip-icon" />
                                                </Popover>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col
                                    xs={24}
                                    sm={24}
                                    md={24}
                                    flex="1 1 auto"
                                    className="keyword-ver1-buttons-col"
                                >
                                    <Space
                                        size="small"
                                        wrap={false}
                                        className="keyword-ver1-buttons"
                                    >
                                        <Button
                                            type="primary"
                                            className="search-btn-premium"
                                            icon={<SearchOutlined />}
                                            onClick={handleSubmit}
                                            loading={loading}
                                        >
                                            조회하기
                                        </Button>
                                        <Button
                                            className="page-action-btn"
                                            icon={<ReloadOutlined />}
                                            onClick={handleReset}
                                        >
                                            초기화
                                        </Button>
                                        <Button
                                            className="history-btn"
                                            icon={<HistoryOutlined />}
                                            onClick={() => {
                                                setShowHistoryModal(true);
                                                fetchHistory(0, false, 'keywordver1');
                                            }}
                                        >
                                            기록
                                        </Button>
                                        <Button
                                            className="history-btn"
                                            icon={<DashboardOutlined />}
                                            onClick={() => {
                                                setShowHistoryModal(true);
                                                fetchHistory(0, false, 'findmid');
                                            }}
                                        >
                                            MID기록
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </form>
                    </Card>
                </div>

                {/* 하단: 결과 영역 - 전체 너비 */}
                <div className="page-content-inner">
                    {!searched ? (
                        <Card
                            bodyStyle={{
                                padding: '48px 24px',
                                textAlign: 'center',
                                background: '#fafafa',
                                borderRadius: '12px',
                            }}
                        >
                            <SearchOutlined
                                style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }}
                            />
                            <Paragraph type="secondary" style={{ margin: 0, fontSize: '15px' }}>
                                메인키워드와 상품번호를 입력한 후 <Text strong>조회하기</Text>{' '}
                                버튼을 클릭하세요.
                            </Paragraph>
                        </Card>
                    ) : productInfo ? (
                        <Row gutter={[24, 24]}>
                            <Col span={24}>
                                <Card className="keyword-ver1-product-card">
                                    <div className="keyword-ver1-product-inner">
                                        <div className="keyword-ver1-thumb-wrap">
                                            <img
                                                src={productInfo.image}
                                                alt={productInfo.title}
                                                className="keyword-ver1-product-thumb"
                                            />
                                        </div>
                                        <div className="keyword-ver1-product-info">
                                            <div className="keyword-ver1-title-row">
                                                <h3 className="keyword-ver1-product-title">
                                                    {productInfo.title}
                                                </h3>
                                                <Button
                                                    type="primary"
                                                    href={productInfo.link}
                                                    target="_blank"
                                                    size="small"
                                                    className="keyword-ver1-detail-btn"
                                                >
                                                    상세 보기
                                                </Button>
                                            </div>
                                            <div className="keyword-ver1-rank-price-block">
                                                <div className="keyword-ver1-rank-row">
                                                    <span className="keyword-ver1-rank-label">
                                                        <b>순위 :</b>
                                                    </span>
                                                    <Badge
                                                        count={
                                                            rankings.find(
                                                                r => r.keyword === keyword
                                                            )?.rank === null
                                                                ? '조회중'
                                                                : `${rankings.find(r => r.keyword === keyword)?.rank || 0}위`
                                                        }
                                                        style={{
                                                            backgroundColor:
                                                                rankings.find(
                                                                    r => r.keyword === keyword
                                                                )?.rank === null
                                                                    ? '#1890ff'
                                                                    : '#2e5bff',
                                                        }}
                                                    />
                                                </div>
                                                <div className="keyword-ver1-price-row">
                                                    <span
                                                        style={{
                                                            fontSize: '13px',
                                                            color: '#1a1a1a',
                                                            fontWeight: 500,
                                                        }}
                                                    >
                                                        <b style={{ fontWeight: 600 }}>가격 :</b>{' '}
                                                        {productInfo.price}원
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="keyword-ver1-meta-block">
                                                <div className="keyword-ver1-meta-line">
                                                    <b>판매처 :</b> {productInfo.mallName}
                                                </div>
                                                <div className="keyword-ver1-meta-line">
                                                    <b>카테고리 :</b> {productInfo.category}
                                                </div>
                                                <div className="keyword-ver1-meta-line">
                                                    <b>상품ID :</b> {productInfo.productId}
                                                </div>
                                                <div className="keyword-ver1-meta-line">
                                                    <b>상품MID :</b> {productInfo.productMid}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Card>

                                {rankings.length > 0 && (
                                    <Row gutter={[16, 16]}>
                                        <Col xs={24} md={8}>
                                            <Card
                                                className="keyword-ver1-result-card"
                                                title={
                                                    <Text strong>
                                                        검색된 키워드 ({keywordList.length}개)
                                                    </Text>
                                                }
                                                extra={
                                                    <Tooltip
                                                        title={`${kwCopy.count}개 복사됨`}
                                                        open={kwCopy.show}
                                                    >
                                                        <Button
                                                            icon={<CopyOutlined />}
                                                            size="small"
                                                            onClick={() =>
                                                                kwCopy.copyArray(keywordList)
                                                            }
                                                        >
                                                            Copy All
                                                        </Button>
                                                    </Tooltip>
                                                }
                                                bodyStyle={{
                                                    padding: '0',
                                                    maxHeight: '500px',
                                                    overflowY: 'auto',
                                                }}
                                                style={{ borderRadius: '12px' }}
                                            >
                                                <List
                                                    dataSource={keywordList}
                                                    renderItem={kw => (
                                                        <List.Item
                                                            onClick={() =>
                                                                handleInlineCopy('kw', kw, kw)
                                                            }
                                                            style={{
                                                                cursor: 'pointer',
                                                                padding: '10px 16px',
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    width: '100%',
                                                                    alignItems: 'center',
                                                                }}
                                                            >
                                                                <Text>{kw}</Text>
                                                                {inlineCopied?.group === 'kw' &&
                                                                    inlineCopied?.id === kw && (
                                                                        <Text
                                                                            type="success"
                                                                            style={{
                                                                                fontSize: '12px',
                                                                            }}
                                                                        >
                                                                            <CheckOutlined /> 복사됨
                                                                        </Text>
                                                                    )}
                                                            </div>
                                                        </List.Item>
                                                    )}
                                                />
                                            </Card>
                                        </Col>
                                        <Col xs={24} lg={16}>
                                            <Card
                                                className="keyword-ver1-result-card"
                                                title={
                                                    <Space>
                                                        <Text strong>
                                                            순위 (
                                                            {rankFilter
                                                                ? visibleRankings.length
                                                                : rankings.length}
                                                            개)
                                                        </Text>
                                                        {rankFilter && (
                                                            <Text type="secondary" size="small">
                                                                / 필터 : {rankFilter}위 이하
                                                            </Text>
                                                        )}
                                                    </Space>
                                                }
                                                extra={
                                                    <Tooltip
                                                        title={`${rankCopy.count}개 복사됨`}
                                                        open={rankCopy.show}
                                                    >
                                                        <Button
                                                            icon={<CopyOutlined />}
                                                            size="small"
                                                            onClick={() =>
                                                                rankCopy.copyArray(
                                                                    visibleRankings.map(
                                                                        r => r.keyword
                                                                    )
                                                                )
                                                            }
                                                        >
                                                            Copy All
                                                        </Button>
                                                    </Tooltip>
                                                }
                                                bodyStyle={{ padding: '0' }}
                                                style={{ borderRadius: '12px' }}
                                            >
                                                <div
                                                    style={{
                                                        padding: '12px',
                                                        borderBottom: '1px solid #f0f0f0',
                                                    }}
                                                >
                                                    <Space wrap>
                                                        <Button
                                                            shape="round"
                                                            size="small"
                                                            type={
                                                                rankFilter === ''
                                                                    ? 'primary'
                                                                    : 'default'
                                                            }
                                                            onClick={() => {
                                                                setRankFilter('');
                                                                setShowCustomInput(false);
                                                            }}
                                                        >
                                                            전체
                                                        </Button>
                                                        {[1, 10].map(num => (
                                                            <Button
                                                                key={num}
                                                                shape="round"
                                                                size="small"
                                                                type={
                                                                    rankFilter === num
                                                                        ? 'primary'
                                                                        : 'default'
                                                                }
                                                                onClick={() => {
                                                                    setRankFilter(num);
                                                                    setShowCustomInput(false);
                                                                }}
                                                            >
                                                                {num}위 이하
                                                            </Button>
                                                        ))}
                                                        {showCustomInput ? (
                                                            <Input
                                                                type="number"
                                                                size="small"
                                                                placeholder="숫자 입력"
                                                                style={{ width: '80px' }}
                                                                onBlur={e => {
                                                                    const val = e.target.value;
                                                                    if (!val)
                                                                        setShowCustomInput(false);
                                                                }}
                                                                onChange={e =>
                                                                    setRankFilter(
                                                                        e.target.value
                                                                            ? Number(e.target.value)
                                                                            : ''
                                                                    )
                                                                }
                                                                value={
                                                                    rankFilter &&
                                                                    ![1, 10].includes(rankFilter)
                                                                        ? rankFilter
                                                                        : ''
                                                                }
                                                            />
                                                        ) : (
                                                            <Button
                                                                shape="round"
                                                                size="small"
                                                                type={
                                                                    ![1, 10, ''].includes(
                                                                        rankFilter
                                                                    )
                                                                        ? 'primary'
                                                                        : 'default'
                                                                }
                                                                onClick={() =>
                                                                    setShowCustomInput(true)
                                                                }
                                                            >
                                                                직접 입력
                                                            </Button>
                                                        )}
                                                    </Space>
                                                </div>
                                                <div
                                                    style={{
                                                        maxHeight: '435px',
                                                        overflowY: 'auto',
                                                    }}
                                                >
                                                    <List
                                                        dataSource={visibleRankings}
                                                        renderItem={r => {
                                                            const id = `${r.keyword}|${r.rank}`;
                                                            const relatedPMs = pMResults.filter(
                                                                p => p.query.keyword === r.keyword
                                                            );
                                                            const checks = relatedPMs[0]?.checks;

                                                            return (
                                                                <List.Item
                                                                    onClick={() =>
                                                                        handleInlineCopy(
                                                                            'rank',
                                                                            id,
                                                                            r.keyword
                                                                        )
                                                                    }
                                                                    style={{
                                                                        cursor: 'pointer',
                                                                        padding: '12px 16px',
                                                                    }}
                                                                >
                                                                    <div style={{ width: '100%' }}>
                                                                        <div
                                                                            style={{
                                                                                display: 'flex',
                                                                                justifyContent:
                                                                                    'space-between',
                                                                                alignItems:
                                                                                    'center',
                                                                            }}
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    fontWeight: 600,
                                                                                }}
                                                                            >
                                                                                {r.keyword} :{' '}
                                                                                {r.rank === null ? (
                                                                                    <span
                                                                                        style={{
                                                                                            color: '#1890ff',
                                                                                            fontSize:
                                                                                                '13px',
                                                                                        }}
                                                                                    >
                                                                                        <LoadingOutlined
                                                                                            style={{
                                                                                                marginRight: 4,
                                                                                            }}
                                                                                        />{' '}
                                                                                        조회중...
                                                                                    </span>
                                                                                ) : (
                                                                                    <Text
                                                                                        style={{
                                                                                            color: '#1890ff',
                                                                                        }}
                                                                                        strong
                                                                                    >
                                                                                        {r.rank ===
                                                                                        0
                                                                                            ? '순위권 밖'
                                                                                            : `${r.rank}위`}
                                                                                    </Text>
                                                                                )}
                                                                                {inlineCopied?.group ===
                                                                                    'rank' &&
                                                                                    inlineCopied?.id ===
                                                                                        id && (
                                                                                        <Text
                                                                                            type="success"
                                                                                            style={{
                                                                                                fontSize:
                                                                                                    '12px',
                                                                                                marginLeft:
                                                                                                    '8px',
                                                                                            }}
                                                                                        >
                                                                                            <CheckOutlined />{' '}
                                                                                            복사됨
                                                                                        </Text>
                                                                                    )}
                                                                            </div>
                                                                            {isProxySearchSession &&
                                                                                !checks && (
                                                                                    <div
                                                                                        style={{
                                                                                            marginLeft:
                                                                                                '12px',
                                                                                        }}
                                                                                    >
                                                                                        <span
                                                                                            style={{
                                                                                                fontSize:
                                                                                                    '11px',
                                                                                                color: '#1890ff',
                                                                                                display:
                                                                                                    'inline-flex',
                                                                                                alignItems:
                                                                                                    'center',
                                                                                            }}
                                                                                        >
                                                                                            <LoadingOutlined
                                                                                                style={{
                                                                                                    marginRight: 4,
                                                                                                }}
                                                                                            />{' '}
                                                                                            통검
                                                                                            조회중...
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                            {checks?.hasPriceCompare && (
                                                                                <div
                                                                                    style={{
                                                                                        marginLeft:
                                                                                            '12px',
                                                                                    }}
                                                                                >
                                                                                    <span
                                                                                        style={{
                                                                                            fontSize:
                                                                                                '11px',
                                                                                            color: '#52c41a',
                                                                                            display:
                                                                                                'inline-flex',
                                                                                            alignItems:
                                                                                                'center',
                                                                                            fontWeight:
                                                                                                '500',
                                                                                        }}
                                                                                    >
                                                                                        통검가격비교{' '}
                                                                                        {checks.productLinkPosition !=
                                                                                        null ? (
                                                                                            <span
                                                                                                style={{
                                                                                                    color: '#1890ff',
                                                                                                    marginLeft:
                                                                                                        '4px',
                                                                                                    fontWeight:
                                                                                                        '600',
                                                                                                }}
                                                                                            >
                                                                                                (
                                                                                                {checks
                                                                                                    .productLinkPosition
                                                                                                    .adRank
                                                                                                    ? `AD ${checks.productLinkPosition.adRank}위`
                                                                                                    : ''}
                                                                                                {checks
                                                                                                    .productLinkPosition
                                                                                                    .adRank &&
                                                                                                checks
                                                                                                    .productLinkPosition
                                                                                                    .normalRank
                                                                                                    ? ' / '
                                                                                                    : ''}
                                                                                                {checks
                                                                                                    .productLinkPosition
                                                                                                    .normalRank
                                                                                                    ? `${checks.productLinkPosition.normalRank}위`
                                                                                                    : ''}
                                                                                                )
                                                                                            </span>
                                                                                        ) : (
                                                                                            <span
                                                                                                style={{
                                                                                                    marginLeft:
                                                                                                        '4px',
                                                                                                    opacity: 0.8,
                                                                                                }}
                                                                                            >
                                                                                                (순위
                                                                                                없음)
                                                                                            </span>
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </List.Item>
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            </Card>
                                        </Col>
                                    </Row>
                                )}
                            </Col>
                        </Row>
                    ) : loading ? (
                        <div style={{ textAlign: 'center', padding: '48px' }}>
                            <Spin size="large" />
                        </div>
                    ) : null}
                </div>
            </div>

            <Modal
                title={historyDataGubun === 'keywordver1' ? '조회 기록' : 'MID 기록'}
                open={showHistoryModal}
                onCancel={() => setShowHistoryModal(false)}
                centered
                mask={{ blur: true }}
                footer={[
                    <Button key="close" onClick={() => setShowHistoryModal(false)}>
                        닫기
                    </Button>,
                ]}
                width={800}
                className="history-modal"
                bodyStyle={{ maxHeight: '600px', overflowY: 'auto' }}
            >
                {!historyLoaded ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                    </div>
                ) : historyData.length === 0 ? (
                    <Empty description="조회 기록이 없습니다." />
                ) : (
                    <List
                        dataSource={historyData}
                        renderItem={item => {
                            const parts = {};
                            item.input.split('|').forEach(pair => {
                                const [key, value] = pair.split('=');
                                parts[key] = value;
                            });

                            return (
                                <List.Item
                                    actions={[
                                        <Button
                                            key="apply"
                                            type="primary"
                                            size="small"
                                            icon={<ArrowRightOutlined />}
                                            onClick={() => {
                                                handleApplyInput(item.input);
                                                setShowHistoryModal(false);
                                            }}
                                        >
                                            {historyDataGubun === 'keywordver1'
                                                ? '적용'
                                                : 'MID 적용'}
                                        </Button>,
                                        <Button
                                            key="delete"
                                            danger
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleDelete(item.id)}
                                        >
                                            삭제
                                        </Button>,
                                    ]}
                                >
                                    <List.Item.Meta
                                        title={
                                            historyDataGubun === 'keywordver1' ? (
                                                <Space>
                                                    <Text strong>
                                                        키워드: {parts.keyword || '-'}
                                                    </Text>
                                                    <Divider type="vertical" />
                                                    <Text>상품번호: {parts.productId || '-'}</Text>
                                                    <Divider type="vertical" />
                                                    <Text type="primary" strong>
                                                        순위: {parts.ranking || '-'}
                                                    </Text>
                                                </Space>
                                            ) : (
                                                <div>
                                                    <Text strong>
                                                        스토어: {parts.channel_name || '-'}
                                                    </Text>
                                                    <br />
                                                    <Text
                                                        type="secondary"
                                                        style={{ fontSize: '13px' }}
                                                    >
                                                        상품명: {parts.product_name || '-'}
                                                    </Text>
                                                </div>
                                            )
                                        }
                                        description={
                                            <div>
                                                {historyDataGubun !== 'keywordver1' && (
                                                    <div style={{ marginBottom: '4px' }}>
                                                        <Text
                                                            type="secondary"
                                                            style={{ fontSize: '12px' }}
                                                        >
                                                            MID: {parts.syncNvMid || '-'} / 원부:{' '}
                                                            {parts.parentId || '-'}
                                                        </Text>
                                                    </div>
                                                )}
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {item.updated_at || item.searched_at}
                                                </Text>
                                            </div>
                                        }
                                    />
                                </List.Item>
                            );
                        }}
                    >
                        {hasMore && historyData.length > 0 && (
                            <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                <Button onClick={() => fetchHistory(offset, true)}>더보기</Button>
                            </div>
                        )}
                    </List>
                )}
            </Modal>
            <KakaoFloatButton />
        </ProtectedMenuPage>
    );
};

export { KeyWordVer1 };

KeyWordVer1.propTypes = {
    title: PropTypes.string,
    style: PropTypes.object,
};
